
import { BanknoteIcon, Shield, FileCheck, FileSearch, Scale, Building2 } from "lucide-react";
import { LandingPageTemplate } from "@/components/layout/LandingPageTemplate";
import { HeroSection } from "@/components/landing/HeroSection";
import { FeatureSection } from "@/components/landing/FeatureSection";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { BenefitSection } from "@/components/landing/BenefitSection";
import { CtaSection } from "@/components/landing/CtaSection";

const NCUAAssessment = () => {
  return (
    <LandingPageTemplate
      title="NCUA Cybersecurity Assessment"
      description="Comprehensive assessment of your credit union's cybersecurity program against NCUA guidance and requirements."
      keywords={[
        "NCUA cybersecurity", 
        "credit union security", 
        "NCUA compliance", 
        "cybersecurity assessment", 
        "ACET assessment", 
        "credit union compliance"
      ]}
      canonicalUrl="/cybersecurity/ncua-assessment/"
      schemaData={{
        name: "NCUA Cybersecurity Assessment Service",
        description: "Comprehensive evaluation of your credit union's cybersecurity program against NCUA guidance and requirements.",
        provider: {
          name: "edgefi",
          sameAs: "https://edgefi.com"
        }
      }}
    >
      <HeroSection
        title="NCUA"
        highlighted="Cybersecurity Assessment"
        description="Evaluate your credit union's security posture against NCUA requirements and industry best practices with our comprehensive assessment."
        ctaText="Schedule Your Assessment"
        secondaryButtonText="Learn More"
        secondaryButtonLink="#process"
        modalTitle="NCUA Assessment Consultation"
        modalDescription="Tell us about your credit union's compliance needs and challenges, and we'll help you prepare for NCUA cybersecurity requirements."
        additionalFields={{
          lead_source: "Website",
          lead_interest: "NCUA Assessment",
          service_interest: "Compliance Assessment"
        }}
      />

      <FeatureSection 
        title="Comprehensive NCUA Assessment"
        subtitle="CREDIT UNION SECURITY"
        description="Our assessment methodology provides a thorough evaluation of your cybersecurity program against NCUA expectations."
        background="light"
        features={[
          {
            title: "ACET Alignment",
            description: "Assessment aligned with the NCUA's Automated Cybersecurity Examination Tool and guidance.",
            icon: <FileCheck className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Regulatory Compliance",
            description: "Evaluation of compliance with relevant requirements including GLBA, FFIEC, and NCUA guidance.",
            icon: <Scale className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Control Effectiveness",
            description: "Detailed testing and validation of security controls to verify operational effectiveness.",
            icon: <Shield className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Documentation Review",
            description: "Comprehensive review of policies, procedures, and program documentation for completeness.",
            icon: <FileSearch className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Vendor Management",
            description: "Assessment of third-party risk management practices and vendor security oversight.",
            icon: <Building2 className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Credit Union Specialization",
            description: "Industry-specific expertise in credit union operations and regulatory requirements.",
            icon: <BanknoteIcon className="h-6 w-6 text-purple-600" />
          }
        ]}
      />

      <ProcessSection 
        id="process"
        title="Our NCUA Assessment Process"
        subtitle="METHODOLOGY"
        description="A structured approach to evaluating your credit union's cybersecurity program and providing actionable recommendations."
        steps={[
          {
            title: "Scoping & Planning",
            description: "Define assessment scope, information requirements, and engagement timeline."
          },
          {
            title: "Documentation Review",
            description: "Evaluate policies, procedures, and program documentation against NCUA expectations."
          },
          {
            title: "Control Testing",
            description: "Validate the implementation and effectiveness of security controls and safeguards."
          },
          {
            title: "Gap Analysis",
            description: "Identify areas of non-compliance and control weaknesses requiring remediation."
          },
          {
            title: "Findings & Recommendations",
            description: "Develop detailed findings and prioritized recommendations for improvement."
          },
          {
            title: "Remediation Planning",
            description: "Create a roadmap for addressing identified gaps and improving security posture."
          }
        ]}
        layout="vertical"
      />

      <BenefitSection 
        title="Benefits of NCUA-Focused Assessment"
        subtitle="VALUE PROPOSITION"
        background="gradient"
        benefits={[
          {
            title: "Exam Readiness",
            description: "Be prepared for NCUA cybersecurity examinations with confidence in your program."
          },
          {
            title: "Regulatory Compliance",
            description: "Ensure alignment with NCUA, FFIEC, and other financial services requirements."
          },
          {
            title: "Risk Reduction",
            description: "Identify and address security vulnerabilities before they can be exploited."
          },
          {
            title: "Member Protection",
            description: "Demonstrate commitment to protecting member data and financial assets."
          },
          {
            title: "Board Assurance",
            description: "Provide board members with confidence in your cybersecurity program."
          },
          {
            title: "Industry Benchmark",
            description: "Compare your security posture against credit union industry standards."
          }
        ]}
      />

      <CtaSection 
        title="Ready to Assess Your Credit Union's Cybersecurity Program?"
        description="Schedule a consultation to discuss your NCUA assessment needs and compliance requirements."
        ctaText="Schedule Your NCUA Assessment"
        modalTitle="NCUA Assessment Consultation"
        modalDescription="Let's discuss your credit union's specific needs and how our NCUA assessment can help ensure compliance."
        additionalFields={{
          lead_source: "Website",
          lead_interest: "NCUA Assessment",
          service_interest: "Compliance Assessment"
        }}
      />
    </LandingPageTemplate>
  );
};

export default NCUAAssessment;
